import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Zap, Mail, Check, Loader2 } from "lucide-react";

const INDUSTRIES = [
  "Technology", "Healthcare", "Finance", "Education", "Marketing",
  "Engineering", "Design", "Sales", "Consulting", "Legal",
  "Manufacturing", "Real Estate", "Media", "Hospitality", "Retail",
  "Non-Profit", "Government", "Logistics", "Energy", "Agriculture"
];

const NewsletterPreferences = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";

  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [selectedIndustries, setSelectedIndustries] = useState([]);

  useEffect(() => {
    fetchPrefs();
  }, []);

  const fetchPrefs = async () => {
    try {
      const res = await fetch(`${API}/newsletter/preferences?email=${encodeURIComponent(email)}&token=${token}`);
      if (res.ok) {
        const data = await res.json();
        setPrefs(data);
        setFrequency(data.frequency || "weekly");
        setSelectedIndustries(data.industry_filters || []);
      } else {
        toast.error("Invalid or expired link");
      }
    } catch { toast.error("Failed to load preferences"); }
    finally { setLoading(false); }
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/newsletter/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, frequency, industry_filters: selectedIndustries })
      });
      if (res.ok) toast.success("Preferences saved!");
      else toast.error("Failed to save");
    } catch { toast.error("Something went wrong"); }
    finally { setSaving(false); }
  };

  const toggleIndustry = (ind) => {
    setSelectedIndustries(prev =>
      prev.includes(ind) ? prev.filter(i => i !== ind) : [...prev, ind].slice(0, 10)
    );
  };

  if (loading) return (
    <div className="min-h-screen bg-obsidian text-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-crimson" />
    </div>
  );

  if (!prefs) return (
    <div className="min-h-screen bg-obsidian text-white flex items-center justify-center p-6">
      <div className="text-center">
        <h2 className="text-xl font-bold mb-2">Invalid Link</h2>
        <p className="text-gray-400">This preferences link is invalid or expired. Please use the link from your newsletter email.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <div className="max-w-xl mx-auto p-6 sm:p-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-crimson flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold">NEWSLETTER PREFERENCES</h1>
            <p className="text-xs text-gray-500">{email}</p>
          </div>
        </div>

        <motion.div
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Frequency */}
          <div className="bg-obsidian-light border border-[#222] p-6" data-testid="frequency-section">
            <h3 className="font-bold mb-1 flex items-center gap-2">
              <Mail className="w-4 h-4 text-crimson" />
              Digest Frequency
            </h3>
            <p className="text-xs text-gray-500 mb-4">How often would you like to receive job updates?</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: "daily", label: "Daily", desc: "Every morning" },
                { value: "weekly", label: "Weekly", desc: "Every Monday" },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFrequency(opt.value)}
                  className={`p-4 border text-left transition-colors ${
                    frequency === opt.value
                      ? "border-crimson bg-crimson/10"
                      : "border-[#333] hover:border-[#555]"
                  }`}
                  data-testid={`freq-${opt.value}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{opt.label}</span>
                    {frequency === opt.value && <Check className="w-4 h-4 text-crimson" />}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Industry Filters */}
          <div className="bg-obsidian-light border border-[#222] p-6" data-testid="industry-filters-section">
            <h3 className="font-bold mb-1">Industry Filters</h3>
            <p className="text-xs text-gray-500 mb-4">
              Select industries to only receive jobs in those sectors. Leave empty for all industries.
              <span className="text-gray-600"> (Max 10)</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {INDUSTRIES.map(ind => {
                const active = selectedIndustries.includes(ind);
                return (
                  <button
                    key={ind}
                    onClick={() => toggleIndustry(ind)}
                    className={`px-3 py-1.5 text-sm border transition-colors ${
                      active
                        ? "border-crimson text-crimson bg-crimson/10"
                        : "border-[#333] text-gray-500 hover:text-white hover:border-[#555]"
                    }`}
                    data-testid={`industry-${ind}`}
                  >
                    {ind}
                  </button>
                );
              })}
            </div>
            {selectedIndustries.length > 0 && (
              <p className="text-xs text-gray-500 mt-3">
                {selectedIndustries.length} selected — you'll only receive jobs in: {selectedIndustries.join(", ")}
              </p>
            )}
          </div>

          {/* Save */}
          <Button
            onClick={savePrefs}
            disabled={saving}
            className="w-full bg-crimson hover:bg-crimson/80 text-white font-bold uppercase tracking-wider py-6"
            data-testid="save-prefs-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Preferences
          </Button>

          {/* Unsubscribe */}
          <div className="text-center pt-4 border-t border-[#222]">
            <a
              href={`${API}/newsletter/unsubscribe?email=${encodeURIComponent(email)}&token=${token}`}
              className="text-xs text-gray-600 hover:text-rose-400 transition-colors"
              data-testid="unsubscribe-link"
            >
              Unsubscribe from all emails
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NewsletterPreferences;
